import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUp, Loader2, Sparkles, Check } from "lucide-react";

const SUGGESTIONS = ["Cut the first 2 seconds", "Move captions to the top", "Make it square", "Turn off the headline"];

function Bubble({ message }) {
  const mine = message.role === "user";
  return (
    <div data-testid={`clip-assistant-message-${message.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${mine ? "rounded-br-sm border border-cyan-500/20 bg-cyan-500/10 text-copy-200" : "rounded-bl-sm border border-surface-800 bg-panel text-copy-200"}`}>
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
        {message.applied?.length > 0 && (
          <ul data-testid={`clip-assistant-applied-${message.id}`} className="mt-2 flex flex-wrap gap-1.5">
            {message.applied.map((item) => <li key={item} className="inline-flex items-center gap-1 rounded-full bg-cyan-500/15 px-2 py-0.5 text-[11px] font-medium text-cyan-300"><Check size={11} /> {item}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ClipEditAssistant({ clipId, getState, onApply, onRender, disabled }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scroll = useRef(null);

  useEffect(() => {
    api.get(`/clips/${clipId}/assistant`).then(({ data }) => setMessages(data.messages)).catch(() => {});
  }, [clipId]);
  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? draft).trim();
    if (!message || busy || disabled) return;
    setDraft("");
    setError("");
    setBusy(true);
    const pending = { id: `pending-${Date.now()}`, role: "user", content: message };
    setMessages((prev) => [...prev, pending]);
    try {
      const { data } = await api.post(`/clips/${clipId}/assistant`, { message, state: getState() });
      setMessages((prev) => [...prev.filter((m) => m.id !== pending.id), ...data.messages]);
      if (Object.keys(data.changes).length) onApply(data.changes);
      if (data.render) onRender();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== pending.id));
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "The edit assistant could not respond. Please try again.");
      setDraft(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="clip-edit-assistant" className="rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.04] p-3 space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10"><Sparkles className="h-4 w-4 text-cyan-400" /></div>
        <div>
          <h3 className="text-sm font-semibold">Tell the AI what to change</h3>
          <p className="text-[11px] text-copy-500">Trim, captions, style, framing and word fixes. Changes update the controls below; press Save to render.</p>
        </div>
      </div>
      {messages.length > 0 && (
        <div ref={scroll} data-testid="clip-assistant-thread" role="log" className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {messages.map((m) => <Bubble key={m.id} message={m} />)}
          {busy && <div data-testid="clip-assistant-thinking" className="flex items-center gap-2 text-xs text-copy-500"><Loader2 size={13} className="animate-spin" /> Working out the edit…</div>}
        </div>
      )}
      {messages.length === 0 && (
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => <button key={s} type="button" data-testid={`clip-assistant-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} disabled={busy || disabled} onClick={() => send(s)} className="rounded-full border border-surface-700 bg-surface-900 px-3 py-1 text-xs text-copy-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-50">{s}</button>)}
        </div>
      )}
      {error && <p data-testid="clip-assistant-error" role="alert" className="text-xs text-amber-500">{error}</p>}
      <div className="flex items-end gap-2 rounded-xl border border-surface-700 bg-panel p-1.5 focus-within:border-cyan-500/60">
        <Textarea data-testid="clip-assistant-composer" value={draft} disabled={busy || disabled} rows={1} maxLength={1000}
          placeholder='e.g. "End it right after she says thank you" or "change “their” to “there”"'
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
          className="min-h-[36px] max-h-24 flex-1 resize-none border-0 bg-transparent px-2 py-1.5 text-sm shadow-none focus-visible:ring-0" />
        <Button data-testid="clip-assistant-send" type="button" size="icon" aria-label="Send edit instruction" disabled={busy || disabled || !draft.trim()} onClick={() => send()} className="h-8 w-8 shrink-0 rounded-full bg-cyan-500 text-black hover:bg-cyan-400">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUp size={15} />}
        </Button>
      </div>
    </section>
  );
}
